/**
 * Pure helpers for the User Management create/edit form (Contact-User, CTU-1.1):
 * form <-> request conversion, the edit diff, and server field-error routing.
 */
import { CONTACT_STATUS, NON_USER_ID } from '@app-types/user';
import { getApiErrorMessage, getApiErrorStatus, getApiFieldErrors } from '@utils/apiError';
import type { AdminUser, CreateUserRequest } from '@app-types/admin';
import type { ContactStatus } from '@app-types/user';
import type {
  AdditionalContactDataKey,
  CanLoginFieldState,
  UserIdFieldState,
  ChangedFieldsOptions,
  ContactStatusFormValue,
  CreatePayloadOptions,
  EditFormOriginal,
  ErrorTargetField,
  FieldErrorSplit,
  UpdateableUserField,
  UserFieldChanges,
  UserFormData,
  UserFormSubmitError,
  UserFormValue
} from '@app-types/userForm';

// AdditionalContactData: the form and requests use `additional_contact_data_N`,
// responses use `additional_contact_dataN` (snakecase-keys does not split digits).
export const ADDITIONAL_CONTACT_DATA_KEYS: readonly AdditionalContactDataKey[] = [
  { formKey: 'additional_contact_data_1', responseKey: 'additional_contact_data1' },
  { formKey: 'additional_contact_data_2', responseKey: 'additional_contact_data2' },
  { formKey: 'additional_contact_data_3', responseKey: 'additional_contact_data3' }
];

// Text fields that legacy rows may hold as null: a null original compares equal to ''.
const NULLABLE_TEXT_FIELDS = new Set<UpdateableUserField>([
  'preferred_name',
  'contact_id',
  'department',
  'cost_center',
  ...ADDITIONAL_CONTACT_DATA_KEYS.map((k) => k.formKey)
]);

const UPDATEABLE_FIELDS: readonly UpdateableUserField[] = [
  'first_name',
  'last_name',
  'preferred_name',
  'email',
  'role_ids',
  'can_login',
  'division_id',
  'contact_id',
  'user_id',
  'contact_status',
  'department',
  'cost_center',
  ...ADDITIONAL_CONTACT_DATA_KEYS.map((k) => k.formKey)
];

const INACTIVE_CANNOT_LOGIN = 'Inactive users cannot login';
const NO_AUTH_METHOD = 'No auth method - select a role first';
const CONTACT_USER_ID_LOCKED = 'Contacts use NON-USER until a role is assigned';
export const CONTACT_STATUS_REQUIRED = 'Contact Status is required';
export const USER_ID_REQUIRED_FOR_ROLES = 'UserID is required when a role is assigned';

export const booleanToSelectValue = (value: boolean | undefined): string => {
  if (value === undefined || value === null) return 'true';
  return value ? 'true' : 'false';
};

export const selectValueToBoolean = (value: string): boolean => value === 'true';

export const isContactStatus = (value: unknown): value is ContactStatus =>
  value === CONTACT_STATUS.ACTIVE || value === CONTACT_STATUS.INACTIVE;

/** Contact Status as the form holds it: a known status, or '' when none is chosen. */
export const toContactStatusFormValue = (value: unknown): ContactStatusFormValue =>
  isContactStatus(value) ? value : '';

/** ContactStatus shown on the edit form: the stored value, or derived from is_active for rows without one. */
export const initialContactStatus = (user: AdminUser | null | undefined): ContactStatus => {
  if (user?.contact_status) return user.contact_status;
  return user?.is_active === false ? CONTACT_STATUS.INACTIVE : CONTACT_STATUS.ACTIVE;
};

const textValue = (value: UserFormValue | undefined): string => (typeof value === 'string' ? value : '');

/** Empty or NON-USER (any case) is the shared default identity. */
export const isDefaultUserId = (value: UserFormValue | undefined): boolean => {
  const text = textValue(value).trim();
  return text === '' || text.toLowerCase() === NON_USER_ID.toLowerCase();
};

/** Empty or NON-USER becomes NON-USER; otherwise the trimmed value. */
export const normalizeUserId = (value: UserFormValue | undefined): string =>
  isDefaultUserId(value) ? NON_USER_ID : textValue(value).trim();

// Optional text left blank is omitted from the create body.
const optionalTextValue = (value: UserFormValue | undefined): string | undefined => {
  const text = textValue(value);
  return text.trim() === '' ? undefined : text;
};

/** The multiselect's role ids (strings) as the numeric ids the API expects. */
export const toRoleIds = (value: UserFormValue | undefined): number[] =>
  Array.isArray(value) ? value.map((id) => Number.parseInt(id, 10)) : [];

/** The role ids the form holds, as strings (the multiselect's option values). */
export const toRoleIdStrings = (value: UserFormValue | undefined): string[] => (Array.isArray(value) ? value : []);

/**
 * Create body (contract 3.1). Never carries is_active. division_id is
 * sent only while the Division field is visible; when it is hidden the key is
 * omitted so the server applies the Unidentified default (D-014, R7).
 */
export const buildCreatePayload = (formData: UserFormData, options: CreatePayloadOptions): CreateUserRequest => {
  // Contact Status is a required select, so the form never submits without one.
  if (!isContactStatus(formData.contact_status)) throw new Error(CONTACT_STATUS_REQUIRED);
  const payload: CreateUserRequest = {
    email: textValue(formData.email),
    first_name: textValue(formData.first_name),
    last_name: textValue(formData.last_name),
    contact_id: textValue(formData.contact_id),
    user_id: normalizeUserId(formData.user_id),
    contact_status: formData.contact_status,
    department: textValue(formData.department),
    cost_center: textValue(formData.cost_center),
    role_ids: toRoleIds(formData.role_ids),
    auth_source: options.authSource,
    can_login: formData.can_login !== undefined ? selectValueToBoolean(String(formData.can_login)) : undefined
  };

  const preferredName = options.canSetPreferredName ? optionalTextValue(formData.preferred_name) : undefined;
  if (preferredName !== undefined) payload.preferred_name = preferredName;

  ADDITIONAL_CONTACT_DATA_KEYS.forEach(({ formKey }) => {
    const value = optionalTextValue(formData[formKey]);
    if (value !== undefined) payload[formKey] = value;
  });

  if (options.divisionVisible) {
    payload.division_id =
      formData.division_id === '' || formData.division_id === undefined ? null : Number(formData.division_id);
  }

  return payload;
};

/**
 * Splits server field errors into those a visible, editable field on the form can
 * show and the rest (hidden or absent fields, e.g. the D-008 division_id error while
 * Division is hidden), which go to a toast.
 */
export const splitFieldErrors = (
  fieldErrors: Record<string, string>,
  fields: readonly ErrorTargetField[]
): FieldErrorSplit => {
  const shown = new Set(fields.filter((f) => !f.hidden && f.type !== 'readonly').map((f) => f.name));
  const onForm: Record<string, string> = {};
  const elsewhere: string[] = [];
  Object.entries(fieldErrors).forEach(([field, message]) => {
    if (shown.has(field)) onForm[field] = message;
    else elsewhere.push(message);
  });
  return { onForm, elsewhere };
};

/**
 * How a failed create/edit is shown. Server field errors go under their fields (the
 * rest to a toast); failing that, a 409 maps to `conflictErrors` when given (the
 * create form's duplicate-email message); anything else becomes a toast.
 */
export const resolveUserFormSubmitError = (
  error: unknown,
  fields: readonly ErrorTargetField[],
  fallbackMessage: string,
  conflictErrors?: Record<string, string>
): UserFormSubmitError => {
  const { onForm, elsewhere } = splitFieldErrors(getApiFieldErrors(error), fields);
  if (Object.keys(onForm).length > 0 || elsewhere.length > 0) {
    return { fieldErrors: onForm, toastMessage: elsewhere.length > 0 ? elsewhere.join(' ') : undefined };
  }
  if (conflictErrors && getApiErrorStatus(error) === 409) {
    return { fieldErrors: conflictErrors };
  }
  return { fieldErrors: {}, toastMessage: getApiErrorMessage(error, fallbackMessage) };
};

export const withoutKey = (errors: Record<string, string>, key: string): Record<string, string> => {
  if (!(key in errors)) return errors;
  const next = { ...errors };
  delete next[key];
  return next;
};

/**
 * Values the form must take after a field change: an Inactive contact cannot log in,
 * so choosing Inactive turns Login Access off. Returns undefined when nothing changes.
 */
export const getContactStatusOverrides = (name: string, value: UserFormValue): Partial<UserFormData> | undefined =>
  name === 'contact_status' && value === CONTACT_STATUS.INACTIVE
    ? { can_login: 'false', user_id: NON_USER_ID, role_ids: [], auth_source: 'None' }
    : undefined;

/** Login Access is locked off without a role (no auth method) or for an Inactive contact. */
export const getCanLoginFieldState = (
  roleIds: readonly string[],
  contactStatus: ContactStatusFormValue
): CanLoginFieldState => {
  if (contactStatus === CONTACT_STATUS.INACTIVE) return { disabled: true, placeholder: INACTIVE_CANNOT_LOGIN };
  if (roleIds.length === 0) return { disabled: true, placeholder: NO_AUTH_METHOD };
  return { disabled: false };
};

/** A contact (no role) cannot hold a real UserID; the field is locked to NON-USER. */
export const getUserIdFieldState = (roleIds: readonly string[]): UserIdFieldState => {
  if (roleIds.length === 0) return { disabled: true, placeholder: CONTACT_USER_ID_LOCKED };
  return { disabled: false, placeholder: 'Enter a unique UserID' };
};

/**
 * Role changes drive UserID so a contact cannot keep a real id, and assigning a
 * role cannot silently reuse NON-USER. Adding a further role leaves the typed id.
 */
export const getRoleChangeUserIdOverrides = (
  name: string,
  value: UserFormValue,
  previousRoleIds: readonly string[]
): Partial<UserFormData> | undefined => {
  if (name !== 'role_ids') return undefined;
  const hasRoles = toRoleIdStrings(value).length > 0;
  if (!hasRoles) return { user_id: NON_USER_ID };
  if (previousRoleIds.length === 0) return { user_id: '' };
  return undefined;
};

const roleIdsChange = (formValue: unknown, originalValue: unknown) => {
  const newValue = Array.isArray(formValue) ? formValue.map((id) => Number.parseInt(String(id), 10)) : [];
  const origValue: unknown[] = Array.isArray(originalValue) ? originalValue : [];
  const hasChanged = newValue.length !== origValue.length || !newValue.every((v) => origValue.includes(v));
  return { hasChanged, newValue };
};

const divisionIdChange = (formValue: unknown, originalValue: unknown) => {
  const newValue = formValue === '' ? null : Number.parseInt(String(formValue), 10);
  const origValue = originalValue ? Number.parseInt(String(originalValue), 10) : null;
  return { hasChanged: newValue !== origValue, newValue };
};

const fieldChange = (
  field: UpdateableUserField,
  formValue: unknown,
  originalValue: unknown
): { hasChanged: boolean; newValue: unknown } => {
  if (field === 'role_ids') return roleIdsChange(formValue, originalValue);
  if (field === 'division_id') return divisionIdChange(formValue, originalValue);
  if (field === 'user_id') {
    const newValue = normalizeUserId(formValue as UserFormValue | undefined);
    return { hasChanged: normalizeUserId(originalValue as UserFormValue | undefined) !== newValue, newValue };
  }

  if (field === 'can_login') {
    const newValue = selectValueToBoolean(String(formValue));
    return { hasChanged: originalValue !== newValue, newValue };
  }

  if (NULLABLE_TEXT_FIELDS.has(field)) {
    const newValue = formValue ?? '';
    return { hasChanged: String(originalValue ?? '') !== String(newValue), newValue };
  }

  return { hasChanged: originalValue !== formValue, newValue: formValue };
};

/**
 * The stored user in edit-form shape, so the diff compares like with like:
 * ContactStatus falls back to is_active, AdditionalContactData uses the request keys.
 */
export const toEditFormOriginal = (user: AdminUser): EditFormOriginal => {
  const original: EditFormOriginal = {
    first_name: user.first_name,
    last_name: user.last_name,
    preferred_name: user.preferred_name,
    email: user.email,
    role_ids: user.role_ids,
    can_login: user.can_login,
    division_id: user.division_id,
    contact_id: user.contact_id,
    user_id: user.user_id,
    contact_status: initialContactStatus(user),
    department: user.department,
    cost_center: user.cost_center
  };
  ADDITIONAL_CONTACT_DATA_KEYS.forEach(({ formKey, responseKey }) => {
    original[formKey] = user[responseKey];
  });
  return original;
};

/** The edit form's starting values for the selected user (empty when none is selected). */
export const toEditFormInitialData = (user: AdminUser | null): UserFormData => {
  const initialData: UserFormData = {
    email: user?.email ?? '',
    first_name: user?.first_name ?? '',
    last_name: user?.last_name ?? '',
    preferred_name: user?.preferred_name ?? '',
    auth_source: user?.auth_source ?? '',
    role_ids: user?.role_ids?.map((id) => id.toString()) || [],
    division_id: user?.division_id?.toString() || '',
    contact_id: user?.contact_id ?? '',
    user_id: user?.role_ids && user.role_ids.length > 0 ? (user.user_id ?? '') : NON_USER_ID,
    contact_status: initialContactStatus(user),
    department: user?.department ?? '',
    cost_center: user?.cost_center ?? '',
    can_login: booleanToSelectValue(user?.can_login)
  };
  ADDITIONAL_CONTACT_DATA_KEYS.forEach(({ formKey, responseKey }) => {
    initialData[formKey] = user?.[responseKey] ?? '';
  });
  return initialData;
};

/**
 * Only the changed fields go into the PUT body. is_active is not sent from the form
 * (ContactStatus replaces it; Deactivate still sends it). When the Division field is
 * hidden, division_id is left out of the diff entirely so the stored division is kept
 * (D-014): it is never sent, not even as a clear.
 */
export const getChangedFields = (
  originalData: EditFormOriginal,
  formData: UserFormData,
  options: ChangedFieldsOptions
): UserFieldChanges => {
  const changes: UserFieldChanges = {};
  const updateableFields = options.includeDivision
    ? UPDATEABLE_FIELDS
    : UPDATEABLE_FIELDS.filter((field) => field !== 'division_id');

  updateableFields.forEach((field) => {
    const formValue = formData[field];
    if (formValue === undefined) return;

    const { hasChanged, newValue } = fieldChange(field, formValue, originalData[field]);
    if (hasChanged) changes[field] = newValue;
  });

  return changes;
};

export const isEligibleForTemporaryPassword = (user: AdminUser): boolean => {
  if (!user.can_login || !user.is_active) return false;
  if (user.auth_source === 'local') return true;
  return user.auth_source === 'keycloak' && !user.graph_user_id;
};
